import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { mutationEditorClear } from 'gql/mutations/editor';
import { useMutationGameUpdate } from 'gql/mutations/game';
import { useQueryCourseChapters } from 'gql/queries/course';
import { useQueryLocalEditor } from 'gql/queries/local';
import extract from 'helpers/extract';
import { trackEvent, reportError } from 'lib/tracking';

import GameUpdate from './GameUpdate';

const GameUpdateContainer = ({ course, ...props }) => {
  const { t } = useTranslation();
  const mutationGameUpdate = useMutationGameUpdate();
  const {
    data: {
      editor: { status: editorStatus, gameEditing },
    },
  } = useQueryLocalEditor();
  const isModalOpen = editorStatus === 'game-update';
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        type: yup
          .string()
          .required(t('Course/Teacher/Games/GameUpdate/type-error-required'))
          .trim()
          .max(64, t('Course/Teacher/Games/GameUpdate/type-error-required')),
        title: yup
          .string()
          .required(t('Course/Teacher/Games/GameUpdate/title-error-required'))
          .trim()
          .max(256, t('Course/Teacher/Games/GameUpdate/title-error-required')),
        chapterId: yup
          .string()
          .trim()
          .matches(
            /^|([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i,
          ),
      }),
    [t],
  );
  const handleGameUpdate = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const variables = extract(values, validationSchema);
        mutationGameUpdate(gameEditing, variables);
        mutationEditorClear();
        trackEvent('game', 'update');
      } catch (err) {
        form.setStatus(err.message);
        reportError('game', 'update', err);
      }
    },
    [gameEditing, mutationGameUpdate, validationSchema],
  );
  const { data } = useQueryCourseChapters({
    variables: {
      courseId: course.id,
    },
    skip: !isModalOpen,
  });
  return (
    <GameUpdate
      chapters={data?.course.chapters}
      game={gameEditing}
      handleGameUpdate={handleGameUpdate}
      handleClose={mutationEditorClear}
      isModalOpen={isModalOpen}
      t={t}
      validationSchema={validationSchema}
      {...props}
    />
  );
};

GameUpdateContainer.propTypes = {
  course: PropTypes.object.isRequired,
};

export default GameUpdateContainer;
